// @flow
import * as React from "react";
import { connect } from "react-redux";
import { withRouter, type RouterHistory } from "react-router-dom";
import Gettext from "../common/Gettext";
import { ModalConsumer, type ShowModal } from "../common/ModalProvider";

import { News, Link, Money, Button, Subscribe } from "./Item";
import ItemInfo from "./ItemInfo";
import ItemTournament from "./ItemTournament";
import ItemNews from "./ItemNews";
import ItemAdvice from "./ItemAdvice";
import ItemCongratulations from "./ItemCongratulations";
import ButtonTradeReal from "./ButtonTradeReal";
import ButtonSubscribe from "./ButtonSubscribe";
import * as Icons from "../Icons/Announcements";

import { mapStateToTime, getCompleted } from "../Tournaments/Timer";
import ButtonSwitcher from "../Tournaments/Account/Card/ButtonSwitcher";

import type { AnnouncementsProps } from "./Bell";
import type { Dispatch } from "../../redux/action";
import ItemNotification from "./ItemNotification";
import CouponIcon from "../Icons/Coupon";
import CompensationIcon from "../Icons/Compensation";
import { gaReducer } from "../GA";

type Props = {
  api: any,
  history: RouterHistory,
  close: () => void,
  dispatch: Dispatch,
  time: string,
  announcements: Array<AnnouncementsProps>,
};

function List({
  announcements,
  api,
  onJoinClick,
  close,
  dispatch,
  ...props
}: Props) {
  if (announcements == null) {
    // такого как бы быть не может
    return "YOU SPIN ME RIGHT ROUND BABY RIGHT ROUND";
  }

  if (announcements.length < 1) {
    return (
      <News>
        <Gettext>No new announcements</Gettext>
      </News>
    );
  }

  return announcements.map((item, index) => {
    switch (item.type) {
      case "tournament":
      case "invited-tournament":
        if (process.env.REACT_APP_DEPLOY_TARGET === "eztrader") {
          return null;
        }

        const isCompleted = getCompleted({
          time: props.time,
          ...item.info,
        });

        if (
          item.info["2x_demo_balance_invite"] &&
          item.info.participation_status !== 1
        ) {
          return (
            <ItemCongratulations
              key={item.type + item.info.id + index}
              tournament={item.info}
            />
          );
        }

        return (
          <ItemTournament
            key={item.type + item.info.id + index}
            {...item.info}
            {...props}
          >
            {isCompleted ? (
              <Gettext>The tournament is finished</Gettext>
            ) : (
              <ModalConsumer>
                {({ showModal }: { showModal: ShowModal }) => (
                  <ButtonSwitcher
                    {...item.info}
                    onTradeClick={async (id) => {
                      gaReducer("BELL_GO_TO_TOURNAMENT", { type: "trade" });
                      const activate = await api.fetchEntity(
                        `/api/tournaments/activate?id=${id}`
                      );

                      if (activate !== null) {
                        api.fetchEntity(`/api/tournaments/list?status=1`);
                        api.fetchEntity(
                          `/api/tournaments/list?status=1&participation_status=1`
                        );
                        await api.fetchEntity(`/api/tournaments/stats`);
                        dispatch({
                          type: "ENGAGE_TOURNAMENT_MODE",
                        });
                        close();
                      }
                    }}
                    onActiveTradeClick={() => {
                      gaReducer("BELL_GO_TO_TOURNAMENT", { type: "trade" });
                      close();
                      props.history.push("/trade");
                    }}
                    onJoinClick={() => {
                      props.history.push(`/tournaments/${item.info.id}`);
                      gaReducer("BELL_GO_TO_TOURNAMENT", { type: "join" });
                    }}
                    flat={true}
                    forBell={true}
                  />
                )}
              </ModalConsumer>
            )}
          </ItemTournament>
        );
      case "news":
        return <ItemNews key={item.type + item.news.slug} {...item.news} />;
      case "verification":
        if (item.info.status === "success") {
          return (
            <ItemInfo
              key={item.type + item.timestamp + index}
              timestamp={item.timestamp}
              icon={<Icons.VerificationSuccess />}
              title={
                <Gettext>
                  Congratulations! You've successfully passed verification!
                </Gettext>
              }
              action={
                <ButtonTradeReal
                  onClick={() => {
                    gaReducer("BELL_SUCCESS_VERIFICATION");
                    close();
                  }}
                />
              }
            >
              <Gettext>
                Now withdrawal of funds and trading on a real account become
                available!
              </Gettext>
            </ItemInfo>
          );
        }

        if (item.info.status === "additional_verifiaction") {
          return (
            <ItemInfo
              key={item.type + item.timestamp + index}
              timestamp={item.timestamp}
              icon={<Icons.VerificationError />}
              title={<Gettext>Additional verification is necessary!</Gettext>}
              action={
                <Button type="trade" to="/verification" onClick={() => close()}>
                  <Gettext>Check out</Gettext>
                </Button>
              }
            >
              <Gettext>Please check the verification page.</Gettext>
            </ItemInfo>
          );
        }

        return (
          <ItemInfo
            key={item.type + item.timestamp + index}
            timestamp={item.timestamp}
            icon={<Icons.VerificationError />}
            title={<Gettext>Verification has been refused</Gettext>}
            details={
              <Link to="/helpdesk/faq#verification" onClick={() => close()}>
                <Gettext>What can I do?</Gettext>
              </Link>
            }
            action={
              <Button type="trade" to="/verification" onClick={() => close()}>
                <Gettext>Try again</Gettext>
              </Button>
            }
          >
            {item.info && item.info.message}
          </ItemInfo>
        );
      case "deposit":
        if (item.info.status === "success") {
          return (
            <ItemInfo
              key={item.type + item.timestamp + index}
              timestamp={item.timestamp}
              icon={<Icons.DepositSuccess />}
              // prettier-ignore
              title={
                  <Gettext>
                    You've received funds in the amount of {
                    <Money currency={item.info.currency || null}>
                      {item.info.amount || 300}
                    </Money>
                  }!
                  </Gettext>
                }
              action={
                <ButtonTradeReal
                  onClick={() => {
                    gaReducer("BELL_GO_TO_SUCCESS_DEPOSIT");
                    close();
                  }}
                />
              }
            />
          );
        }

        return (
          <ItemInfo
            key={item.type + item.timestamp + index}
            timestamp={item.timestamp}
            icon={<Icons.DepositError />}
            title={
              <Gettext>Your deposit was not credited to your account</Gettext>
            }
            action={
              <Button type="trade" to="/fillup" onClick={() => close()}>
                <Gettext>Try again</Gettext>
              </Button>
            }
          >
            {item.info && item.info.message}
          </ItemInfo>
        );
      case "withdraw":
        if (item.info.status === "success") {
          return (
            <ItemInfo
              key={item.type + item.timestamp + index}
              timestamp={item.timestamp}
              icon={<Icons.WithdrawSuccess />}
              // prettier-ignore
              title={
                  <Gettext>
                    The funds in the amount of {
                    <Money currency={item.info.currency || null}>
                      {item.info.amount || 300}
                    </Money>
                  } have been successfully withdrawn!
                  </Gettext>
                }
              action={
                <ButtonTradeReal
                  onClick={() => {
                    gaReducer("BELL_GO_TO_SUCCESS_WITHDRAWAL");
                    close();
                  }}
                />
              }
            />
          );
        }

        return (
          <ItemInfo
            key={item.type + item.timestamp + index}
            timestamp={item.timestamp}
            icon={<Icons.WithdrawError />}
            title={
              <Gettext>
                Unfortunately, your withdrawal request has been refused.
              </Gettext>
            }
            action={
              <Button type="trade" to="/withdraw" onClick={() => close()}>
                <Gettext>Try again</Gettext>
              </Button>
            }
          >
            {item.info && item.info.message}
          </ItemInfo>
        );
      case "unconfirmed_push_subscription":
        return (
          <ItemInfo
            key={item.type + item.timestamp + index}
            timestamp={item.timestamp}
            icon={<Icons.PushSubscription />}
            title={
              <Gettext>
                Still not receiving our push notifications about the most
                important events on our platform?
              </Gettext>
            }
            action={
              <Button
                type="trade"
                to="/helpdesk/skilled#news_emails"
                onClick={() => close()}
              >
                <Gettext>How to subscribe?</Gettext>
              </Button>
            }
          >
            <Gettext>Act fast and subscribe!</Gettext>
          </ItemInfo>
        );
      case "unconfirmed_email_subscription":
        return (
          <ItemInfo
            key={item.type + item.timestamp + index}
            timestamp={item.timestamp}
            icon={<Icons.EmailSubscription />}
            title={
              <Gettext>
                Still not receiving our e-mails about the most important events
                on our platform?
              </Gettext>
            }
            action={
              <ButtonSubscribe onClick={() => close()}>
                <Subscribe>
                  <Gettext>SUBSCRIBE</Gettext>
                </Subscribe>
              </ButtonSubscribe>
            }
          >
            <Gettext>Act fast and subscribe!</Gettext>{" "}
            <Gettext>
              We have prepared strategies, trading signals and more for your
              successful trading.
            </Gettext>
          </ItemInfo>
        );

      case "promocode":
        return (
          <ItemNotification
            key={item.type + item.timestamp + index}
            timestamp={item.timestamp}
            icon={<CouponIcon />}
            title={
              <Link
                to={"/fillup?promocode=" + item.info.code}
                onClick={() => gaReducer("BELL_GO_TO_PROMO")}
              >
                <Gettext>
                  We give you a promo code for the special bonus on depositing.
                </Gettext>
              </Link>
            }
          />
        );
      case "compensations":
        return (
          <ItemNotification
            key={item.type + item.timestamp + index}
            timestamp={item.timestamp}
            icon={<CompensationIcon />}
            // prettier-ignore
            title={
                <Link to="/trade" onClick={() => gaReducer("BELL_GO_TO_SAFE_DEALS")}>
                  <Gettext>
                    You have been granted {item.info.used + item.info.available} safe trades.
                  </Gettext>
                </Link>
              }
          />
        );
      case "2x_demo_money":
        return process.env.REACT_APP_DEPLOY_TARGET !== "eztrader" ? (
          <ItemAdvice key={item.type + item.timestamp + index} api={api} />
        ) : null;
      default:
        return null;
    }
  });
}

export default withRouter(connect(mapStateToTime)(List));



// WEBPACK FOOTER //
// ./src/components/Bell/List.js